import { computed, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AmlAdminErrorHandlerService } from '@core/services/aml-admin-error-handler.service';
import { EmailCommunicationService } from '@core/services/email-communication.service';
import {
  AvailableEmailStatuses,
  type EmailListTableColumnSortKey,
} from '@features/email-communication/enums/email-list.enums';
import { EmailListTableConfigService } from '@features/email-communication/services/email-list-table-config.service';
import { AvailableEmailTypes } from '@features/organization/components/delivery-tracker-detail/enums/delivery-tracker-email.enums';
import { DeliveryTrackerDetailStore } from '@features/organization/components/delivery-tracker-detail/stores/delivery-tracker-detail.store';
import { DeliveryTrackerDetailUiStore } from '@features/organization/components/delivery-tracker-detail/stores/delivery-tracker-detail-ui.store';
import { DeliveryTrackerEmailFilterStore } from '@features/organization/components/delivery-tracker-detail/stores/delivery-tracker-email-filter.store';
import type { RiskAnalysisDeliveryFilterState } from '@features/organization/components/delivery-tracker-detail/types/delivery-tracker.types';
import type { UUID } from '@kerberos-compliance/compliance-shared-ts/common/types/uuid.type';
import type { ResponseEmailDto } from '@kerberos-compliance/compliance-shared-ts/features/communication/dtos/response.email.dto';
import type { ListRequestQueryParameters } from '@kerberos-compliance/compliance-shared-ui';
import { BaseFacadeService, DatatableBaseConfig, ErrorSeverityMap } from '@kerberos-compliance/compliance-shared-ui';

@Injectable()
export class DeliveryTrackerEmailFacade extends BaseFacadeService<
  ResponseEmailDto,
  RiskAnalysisDeliveryFilterState,
  EmailListTableColumnSortKey,
  ListRequestQueryParameters<RiskAnalysisDeliveryFilterState>
> {
  protected override readonly componentName = 'DeliveryTrackerEmailFacade';

  private readonly tableConfigService = inject(EmailListTableConfigService);

  protected override readonly filterStore = inject(DeliveryTrackerEmailFilterStore);

  private readonly emailCommunicationService = inject(EmailCommunicationService);

  private readonly uiStore = inject(DeliveryTrackerDetailUiStore);

  private readonly deliveryTrackerDetailStore = inject(DeliveryTrackerDetailStore);

  protected override readonly errorHandlerService = inject(AmlAdminErrorHandlerService);

  public readonly tableColumns = this.tableConfigService.tableColumns;

  public readonly emailTrackerById = computed(() => {
    const trackerId = this.deliveryTrackerDetailStore.selectedPeriodId();
    return trackerId ? this.deliveryTrackerDetailStore.getEmailsForTracker(trackerId) : [];
  });

  /**
   * Sets the period range for filtering emails.
   *
   * @param {Date | undefined} periodStart - The start date of the period range.
   * @param {Date | undefined} periodEnd - The end date of the period range.
   * @returns {void} This method does not return a value.
   */
  public setPeriodRange(periodStart: Date | undefined, periodEnd: Date | undefined): void {
    this.filterStore.setPeriodRange(periodStart, periodEnd);
  }

  /**
   * Sets up filter subscription and reset filter listener before the base initialization runs.
   *
   * @returns {void} This method does not return a value.
   */
  protected override onBeforeInitialize(): void {
    this.setupFilterSubscription(this.filterStore.filter$);
    this.setResetFilter();
  }

  /**
   * Resets all filters to their default state and triggers a fresh data fetch.
   *
   * @returns {void} This method does not return a value.
   */
  public override resetFilters(): void {
    const trackerId = this.deliveryTrackerDetailStore.selectedPeriodId();
    if (trackerId) {
      this.deliveryTrackerDetailStore.clearEmailsForTracker(trackerId);
    }
    this.sortService.reset();
    this.sortState.set(undefined);
    this.filterStore.resetFilters();
  }

  /**
   * Builds the request filters for the email list API.
   *
   * @returns {RiskAnalysisDeliveryFilterState} The current filter payload.
   */
  protected override buildFilters(): RiskAnalysisDeliveryFilterState {
    const filters = this.filterStore.filterState();
    const status = filters.status?.length ? filters.status : AvailableEmailStatuses;
    const type = filters.type?.length ? filters.type : AvailableEmailTypes;

    return {
      ...filters,
      status,
      type,
    };
  }

  /**
   * Fetches email data using the provided parameters.
   *
   * @param {ListRequestQueryParameters<RiskAnalysisDeliveryFilterState>} parameters - Query parameters including filters, pagination, and sorting.
   * @returns {Promise<void>} A promise that resolves once the email data is fetched.
   */
  protected override fetchData(parameters: ListRequestQueryParameters<RiskAnalysisDeliveryFilterState>): Promise<void> {
    return this.fetchEmailList(parameters);
  }

  /**
   * Fetches the email list with centralized error handling and an empty fallback response.
   *
   * @param {ListRequestQueryParameters<RiskAnalysisDeliveryFilterState>} parameters - Query parameters for fetching emails.
   * @returns {Promise<void>} A promise that resolves once the email list request completes.
   */
  private async fetchEmailList(parameters: ListRequestQueryParameters<RiskAnalysisDeliveryFilterState>): Promise<void> {
    await this.executeSafely(() => this.executeEmailListFetch(parameters), 'Fetching email list', {
      onError: () =>
        this.replacePaginatedResponse({
          data: [],
          totalCount: 0,
          hasMore: false,
        }),
    });
  }

  /**
   * Executes the email list fetch with caching logic.
   * Skips the API call if emails are already cached and no filters or sorting are applied.
   *
   * @param {ListRequestQueryParameters<RiskAnalysisDeliveryFilterState>} parameters - Query parameters including filters and pagination.
   * @returns {Promise<void>} A promise that resolves once the email list is fetched and stored.
   */
  private async executeEmailListFetch(
    parameters: ListRequestQueryParameters<RiskAnalysisDeliveryFilterState>,
  ): Promise<void> {
    const trackerId = this.deliveryTrackerDetailStore.selectedPeriodId();

    if (!trackerId) {
      this.errorHandlerService.handleError({
        error: 'No Tracker ID Found',
        metadata: {
          severity: ErrorSeverityMap.High,
          component: 'DeliveryTrackerEmailFacade',
          action: 'No tracker ID found for the selected period when fetching email list',
        },
      });
      return;
    }

    try {
      this.uiStore.setLoadingByTracker(trackerId, true);

      const hasFilters = Boolean(parameters.filters && Object.keys(parameters.filters).length > 0);
      const hasSortParameters =
        ('sortField' in parameters && parameters.sortField) || ('sortOrder' in parameters && parameters.sortOrder);

      if (this.deliveryTrackerDetailStore.hasEmailsForTracker(trackerId) && !hasFilters && !hasSortParameters) {
        return;
      }

      const currentPage = parameters.page ?? this.currentPage();

      const records = await this.emailCommunicationService.fetchDeliveryTrackerEmails(trackerId, {
        ...parameters,
        pageSize: parameters.pageSize ?? this.pageSize,
        page: currentPage,
      });

      if (records) {
        this.deliveryTrackerDetailStore.setEmailsForTracker(trackerId, records.data);
        this.setPaginatedResponse(records, currentPage > DatatableBaseConfig.pageIndex);
      }
    } finally {
      this.uiStore.setLoadingByTracker(trackerId, false);
    }
  }

  /**
   * Checks if a specific period is currently loading emails.
   *
   * @param {UUID} trackerId - The tracker ID to check.
   * @returns {boolean} True if the tracker is currently loading.
   */
  public isLoadingForTracker(trackerId: UUID): boolean {
    return this.uiStore.isLoadingByTracker(trackerId);
  }

  /**
   * Subscribes to the reset trigger and makes a fresh API call.
   *
   * @returns {void} This method does not return a value.
   */
  private setResetFilter(): void {
    this.filterStore.resetTriggered$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.resetFilters());
  }
}
